import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import type { ApiError, PricingConfigurationValidationResponse } from '@/shared/api';
import { useApi } from '@/shared/api';
import { configurationRequests, type Busy } from './pricingConfigurationRequests';
import type { SavedOutcome } from './releaseRailModel';
import { usePricingStrategyProfiles } from './usePricingStrategyProfiles';

export type { SaveResult } from './pricingConfigurationRequests';

export function usePricingConfiguration(propertyId?: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [validation, setValidation] = useState<PricingConfigurationValidationResponse>();
  const [checkError, setCheckError] = useState<ApiError>();
  const [saveError, setSaveError] = useState<ApiError>();
  const [previewError, setPreviewError] = useState<ApiError>();
  const [busy, setBusy] = useState<Busy>(null);
  const [saved, setSaved] = useState<SavedOutcome>();
  // Eine Antwort zu einem Apartment, das inzwischen nicht mehr gewählt ist, landet nirgends.
  const current = useRef(propertyId);
  useEffect(() => {
    current.current = propertyId;
    setValidation(undefined);
    setCheckError(undefined);
    setSaveError(undefined);
    setPreviewError(undefined);
    setSaved(undefined);
  }, [propertyId]);
  const configuration = useQuery({
    queryKey: ['pricing-configuration', propertyId],
    queryFn: () => api.pricingConfigurations.get(propertyId!),
    enabled: Boolean(propertyId),
  });
  const { profiles, activeProfile } = usePricingStrategyProfiles(
    propertyId,
    configuration.isSuccess,
    configuration.data?.configuration.strategyProfileVersionId,
  );
  const refetch = async () => {
    const result = await configuration.refetch();
    return result.isSuccess ? result.data : undefined;
  };
  const clearErrors = () => {
    setCheckError(undefined);
    setSaveError(undefined);
    setPreviewError(undefined);
  };
  /** „Neuen Stand laden“: ist er da, gelten die alten Gründe nicht mehr. */
  const reload = async () => {
    const id = propertyId;
    if ((await refetch()) && current.current === id) clearErrors();
  };
  const requests = configurationRequests({
    api,
    queryClient,
    propertyId,
    base: configuration.data,
    refetch,
    still: (id) => current.current === id,
    begin: (state) => {
      setBusy(state);
      clearErrors();
    },
    end: () => setBusy(null),
    setValidation,
    setCheckError,
    setPreviewError,
    setSaveError,
    setSaved,
  });

  return {
    configuration,
    profiles,
    activeProfile,
    ...requests,
    validation,
    checkError,
    previewError,
    saveError,
    saved,
    isValidating: busy === 'validating',
    isPreviewing: busy === 'previewing',
    isSaving: busy === 'saving',
    reload: () => void reload(),
  };
}
